import { supabase } from "./supabase";

const STORAGE_KEY = "mori.partner_attribution";
const ENGAGEMENT_GATE_MS = 3000;
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";

interface PartnerAttribution {
  code: string;
  scan_session_id: string;  // crypto.randomUUID() — stable per first scan
  scanned_at: string;       // ISO timestamp of FIRST contact; never updated on revisits
}

function readToken(): PartnerAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PartnerAttribution) : null;
  } catch {
    return null;
  }
}

function writeToken(token: PartnerAttribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(token));
  } catch { /* storage blocked */ }
}

function clearToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* storage blocked */ }
}

function startEngagementGate(token: PartnerAttribution): void {
  let fired = false;

  // Defined before `fire` so the hoisting order is explicit.
  function cleanup(): void {
    clearTimeout(timer);
    window.removeEventListener("click", fire);
    window.removeEventListener("touchstart", fire);
    window.removeEventListener("scroll", fire);
  }

  function fire(): void {
    if (fired) return;
    fired = true;
    cleanup();
    void fetch(`${SUPABASE_URL}/functions/v1/log-partner-scan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: token.code,
        scan_session_id: token.scan_session_id,
        user_agent: navigator.userAgent,
      }),
      keepalive: true,  // survives quick navigation away
    });
  }

  const timer = setTimeout(fire, ENGAGEMENT_GATE_MS);
  const opts = { once: true, passive: true } as const;
  window.addEventListener("click", fire, opts);
  window.addEventListener("touchstart", fire, opts);
  window.addEventListener("scroll", fire, opts);
}

/**
 * Call once on app mount. Reads `?ref=<code>` from the URL, writes (or
 * reuses) the localStorage attribution token, strips the param, and starts
 * the 3-second engagement gate.
 */
export function captureRefParam(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (!code) return;

    const existing = readToken();
    let token: PartnerAttribution;

    if (existing && existing.code === code) {
      // Same code on revisit — reuse existing token to keep the true
      // first-contact timestamp. Server dedupes by scan_session_id anyway.
      token = existing;
    } else {
      // New code (or first visit) — overwrite entirely; last-scan-wins.
      token = {
        code,
        scan_session_id: crypto.randomUUID(),
        scanned_at: new Date().toISOString(),
      };
      writeToken(token);
    }

    // Strip ?ref without triggering a navigation.
    params.delete("ref");
    const qs = params.toString();
    const newUrl =
      window.location.pathname +
      (qs ? "?" + qs : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);

    startEngagementGate(token);
  } catch {
    /* storage or history API blocked — degrade silently */
  }
}

/**
 * Call after auth completes. If a pending attribution token exists, POST to
 * claim-partner-referral. Clears the token on success or terminal failure;
 * keeps it on transient errors so the next sign-in retries.
 */
export async function claimAttributionIfPending(): Promise<void> {
  const token = readToken();
  if (!token) return;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return; // no live session — keep token

    const res = await fetch(`${SUPABASE_URL}/functions/v1/claim-partner-referral`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        code: token.code,
        scanned_at: token.scanned_at,
      }),
    });

    // Auth errors (401) and server errors (5xx) are transient — keep token.
    if (res.status === 401 || res.status >= 500) return;

    const body = await res.json() as { attributed: boolean; reason?: string };

    // Clear on success or on any terminal non-retryable outcome.
    if (
      body.attributed ||
      body.reason === "expired" ||
      body.reason === "invalid_code" ||
      body.reason === "missing"
    ) {
      clearToken();
    }
    // reason "error" / undefined → keep token for retry on next sign-in
  } catch {
    /* network error — keep token */
  }
}
